import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Home, ArrowLeft, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const serviceOptions = [
  { value: 'general', fr: 'Demande générale', en: 'General inquiry' },
  { value: 'security', fr: 'Surveillance & Sécurité', en: 'Monitoring & Security' },
  { value: 'maintenance', fr: 'Entretien & Gestion Artisans', en: 'Maintenance & Contractor Management' },
  { value: 'arrival', fr: 'Préparation à l\'Arrivée / location', en: 'Arrival / Rental Preparation' },
  { value: 'lifestyle', fr: 'Conciergerie Lifestyle Premium', en: 'Premium Lifestyle Concierge' },
];

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get('service') || '';
  const [language] = useState(localStorage.getItem('language') || 'fr');
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    lastName: '',
    firstName: '',
    phone: '',
    email: '',
    service: preselected,
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.lastName || !form.firstName || !form.phone || !form.email || !form.service) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs obligatoires.' : 'Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/contact-request`, {
        last_name: form.lastName,
        first_name: form.firstName,
        phone: form.phone,
        email: form.email,
        service: form.service,
        message: form.message,
      });
      toast.success(language === 'fr' ? 'Message envoyé avec succès !' : 'Message sent successfully!');
      setTimeout(() => navigate('/'), 1500);
    } catch {
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi. Veuillez réessayer.' : 'Error sending message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const t = (key) => {
    const translations = {
      fr: {
        title: 'Contactez-nous',
        subtitle: 'Décrivez votre besoin, nous vous répondrons dans les plus brefs délais.',
        lastName: 'Nom',
        firstName: 'Prénom',
        phone: 'Numéro de téléphone',
        email: 'Email',
        service: 'Type de demande',
        message: 'Détails supplémentaires',
        submit: 'Envoyer',
        back: 'Retour',
      },
      en: {
        title: 'Contact Us',
        subtitle: 'Describe your needs and we\'ll get back to you shortly.',
        lastName: 'Last Name',
        firstName: 'First Name',
        phone: 'Phone Number',
        email: 'Email',
        service: 'Request Type',
        message: 'Additional Details',
        submit: 'Send',
        back: 'Back',
      },
    };
    return translations[language]?.[key] || key;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="3+ Conciergerie" className="h-10 w-10 object-contain" />
              <span className="font-heading font-bold text-xl text-foreground">Conciergerie</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" data-testid="contact-back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('back')}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Form */}
      <section className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-3" data-testid="contact-title">
              {t('title')}
            </h1>
            <p className="text-muted-foreground">{t('subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-lg border border-border/50 space-y-6" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="contact-lastName">{t('lastName')} *</Label>
                <Input
                  id="contact-lastName"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  required
                  data-testid="contact-last-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-firstName">{t('firstName')} *</Label>
                <Input
                  id="contact-firstName"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  required
                  data-testid="contact-first-name"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="contact-phone">{t('phone')} *</Label>
                <Input
                  id="contact-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required
                  data-testid="contact-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email">{t('email')} *</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="contact-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('service')} *</Label>
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })} required>
                <SelectTrigger data-testid="contact-service-select">
                  <SelectValue placeholder={t('service')} />
                </SelectTrigger>
                <SelectContent>
                  {serviceOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {language === 'fr' ? opt.fr : opt.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-message">{t('message')}</Label>
              <Textarea
                id="contact-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                data-testid="contact-message"
              />
            </div>

            <Button type="submit" size="lg" className="w-full btn-primary" disabled={submitting} data-testid="contact-submit-btn">
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  {language === 'fr' ? 'Envoi...' : 'Sending...'}
                </span>
              ) : (
                <>
                  {t('submit')}
                  <Send className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
